/* eslint-disable ember/no-computed-properties-in-native-classes */
import { action, computed } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismTable } from '@mylighthouse/prism-ember';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import type { Dayjs } from 'dayjs';
import type { Compset, Hotel, Market } from 'frontend-utils/models';
import MarketRatesTablesCellsTdHotelname from 'lumen/components/market/rates/tables/cells/td-hotelname';
import MarketRatesTablesCellsTdRate from 'lumen/components/market/rates/tables/cells/td-rate';
import MarketRatesTablesCellsThDate from 'lumen/components/market/rates/tables/cells/th-date';
import type { Rate, Ratesummary } from 'lumen/models';
import { NUM_OF_DAYS } from 'lumen/routes/market/marketrates';
import type {
  MealTypeValues,
  MembershipType,
  RateParam,
} from 'lumen/utils/constants/general-constants';

const MARKET_RATE_COLUMNS = {
  SUBSCRIPTION: 'subscription',
};

export interface RatesParams {
  compareType: string;
  compareValue: string;
  los: number;
  maxPersons: number;
  mealPlan: MealTypeValues;
  membershipType: MembershipType;
  month: string;
  ota: string;
  otaId: string;
  rateParam: RateParam;
}

type RatesPerPeriod = Map<string, Rate>;
type PeriodsMapPerHotelinfo = Map<string, RatesPerPeriod>;

interface MarketRatesTablesTableMarketRatesSignature {
  Args: {
    changeRowId: (rowId: string) => void;
    expandedRowIds: string[];
    message: {
      exportDisabled: boolean;
      hasMessage: boolean;
      message: string;
    } | null;
    model: {
      expandedRatesummaries: Record<
        string,
        { data: PeriodsMapPerHotelinfo; isLoading: boolean }
      >;
      loading: boolean;
      marketOrder: string[];
      numOfRatesRequestRunning: number;
      ratesummaries: Ratesummary[];
    };
    params: RatesParams;
    startDate: Dayjs;
  };
  Element: HTMLDivElement;
}

export default class MarketRatesTablesTableMarketRates extends Component<MarketRatesTablesTableMarketRatesSignature> {
  @service declare store: Services['store'];

  @computed(
    // eslint-disable-next-line ember/use-brace-expansion
    'args.model.{loading,numOfRatesRequestRunning}',
    'args.params.los',
    'args.{changeRowId,startDate}',
    'isRowExpanded',
    'isRowLoading',
  ) // Don't remove until marketrates route is refactored
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get columns() {
    const columns: CustomEmberTableColumn[] = [
      {
        name: '',
        valuePath: MARKET_RATE_COLUMNS.SUBSCRIPTION,
        thComponent: MarketRatesTablesCellsThDate,
        tdComponent: MarketRatesTablesCellsTdHotelname,
        params: this.args.params,
        changeRowId: this.args.changeRowId,
        minWidth: 240,
        width: 240,
        maxWidth: 240,
        isFixed: 'left',
        isFirstColumn: true,
        hasRightBorder: true,
        // eslint-disable-next-line @typescript-eslint/unbound-method
        isRowExpanded: this.isRowExpanded,
        // eslint-disable-next-line @typescript-eslint/unbound-method
        isRowLoading: this.isRowLoading,
      },
    ];

    for (let i = 0; i < NUM_OF_DAYS; i++) {
      const date = this.args.startDate.add(i, 'days');
      const formattedDate = date.format('YYYY-MM-DD');
      const isoWeekday = date.isoWeekday();
      const isWeekend = isoWeekday === 5 || isoWeekday === 6;
      columns.push({
        name: formattedDate,
        valuePath: formattedDate,
        periodId: `${formattedDate},,${this.args.params.los},`,
        thComponent: MarketRatesTablesCellsThDate,
        tdComponent: MarketRatesTablesCellsTdRate,
        params: this.args.params,
        minWidth: 55,
        hasRightBorder: isoWeekday === 4 || isWeekend,
        thClassNames: isWeekend ? 'bg-level-1' : '',
        tdClassNames: isWeekend ? 'bg-level-1' : '',
        isLoading: this.args.model.loading,
      });
    }

    return columns;
  }

  @computed(
    // eslint-disable-next-line ember/use-brace-expansion
    'args.model.ratesummaries.length',
    'args.model.{loading,marketOrder,numOfRatesRequestRunning}',
    'marketHash',
  ) // Don't remove until marketrates route is refactored
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get groupedModel() {
    if (this.args.model.loading || !this.args.model.ratesummaries.length) {
      return [];
    }

    const groupedModel: Ratesummary[][] = [];
    this.args.model.marketOrder.forEach((marketId) => {
      groupedModel.push(this.marketHash.get(marketId) as Ratesummary[]);
    });

    return groupedModel;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get marketHash() {
    const marketHash = new Map<string, Ratesummary[]>();

    this.args.model.ratesummaries.forEach((ratesummary) => {
      const marketId = ratesummary.market.id!;

      if (!marketHash.get(marketId)) {
        marketHash.set(marketId, []);
      }

      marketHash.get(marketId)?.push(ratesummary);
    });

    // Sort the RateSummaries by period
    marketHash.forEach((ratesummaries) => {
      ratesummaries.sort((a, b) => a.period.day.date.diff(b.period.day.date));
    });

    return marketHash;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get overlayMessage() {
    if (!this.args.message) {
      return undefined;
    }

    return this.args.message.message;
  }

  @computed(
    // eslint-disable-next-line ember/use-brace-expansion
    'args.expandedRowIds.[]',
    'args.model.{expandedRatesummaries,loading,numOfRatesRequestRunning}',
    'args.params.otaId',
    'groupedModel',
  ) // Don't remove until marketrates route is refactored
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rows() {
    const rows: CustomEmberTableRow[] = [];
    this.groupedModel.forEach((ratesummaryArray) => {
      const currentRateSummary = ratesummaryArray[0] as Ratesummary;

      const row = {
        [MARKET_RATE_COLUMNS.SUBSCRIPTION]: currentRateSummary,
      };

      ratesummaryArray.forEach((ratesummary) => {
        row[ratesummary.period.day.date.format('YYYY-MM-DD')] = ratesummary;
      });

      rows.push(row);

      // Add Rates for expanded Ratesummary rows
      const marketId = currentRateSummary.market.id!;
      if (this.args.expandedRowIds.includes(marketId)) {
        const expandedRatesummary =
          this.args.model.expandedRatesummaries[marketId];

        if (!expandedRatesummary || expandedRatesummary.isLoading) {
          return;
        }

        // Fetch order as defined in the compset
        const compset = this.getCompsetOfMarket(marketId);

        if (!compset) {
          return;
        }

        const competitors = compset.competitors.filter(
          (competitor) =>
            competitor.otas.find(
              (ota) => ota.id === this.args.params?.otaId,
            ) !== undefined,
        );

        competitors.forEach((competitor) => {
          const ratesPerHotelinfo = expandedRatesummary.data.get(
            competitor.id!,
          );

          if (!ratesPerHotelinfo) {
            return;
          }

          const childRow = {
            [MARKET_RATE_COLUMNS.SUBSCRIPTION]: ratesPerHotelinfo.get(
              currentRateSummary.period.id!,
            ),
            className: 'table-row--level-1',
          };

          ratesummaryArray.forEach((ratesummary) => {
            const rate = ratesPerHotelinfo.get(ratesummary.period.id!) as Rate;
            rate.ratesummary = ratesummary; // Link Rate to Ratesummary
            childRow[ratesummary.period.day.date.format('YYYY-MM-DD')] = rate;
          });

          rows.push(childRow);
        });
      }
    });

    return rows;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getCompsetOfMarket(marketId: string) {
    const market = this.store.peekRecord<Market>('market', marketId);

    if (!market) {
      return undefined;
    }

    const hotelForMarket = market.hotel as Hotel;
    return hotelForMarket.compsets.filter(
      (compset) => compset.isPrimary,
    )[0] as Compset;
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  isRowExpanded(marketId: string) {
    return this.args.expandedRowIds.includes(marketId);
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  isRowLoading(marketId: string) {
    return (
      this.isRowExpanded(marketId) &&
      this.args.model.expandedRatesummaries[marketId]?.isLoading
    );
  }

  <template>
    <div
      class="table-market-rates"
      data-test-id="table-market-rates"
      ...attributes
    >
      <PrismTable
        @columns={{this.columns}}
        @isLoading={{@model.loading}}
        @overlayMessage={{this.overlayMessage}}
        @rows={{this.rows}}
        @widthConstraint="eq-container"
      />
    </div>
  </template>
}
